import React, { useState } from 'react';

const Avatar = ({
  src,
  alt,
  fallback,
  size = 'md',
  className = '',
  children,
  ...props
}) => {
  const [imageError, setImageError] = useState(false);

  // Size variants
  const sizeClasses = {
    xs: 'h-6 w-6 text-xs',
    sm: 'h-8 w-8 text-sm',
    md: 'h-10 w-10 text-base',
    lg: 'h-12 w-12 text-lg',
    xl: 'h-16 w-16 text-xl',
    '2xl': 'h-20 w-20 text-2xl'
  };

  // Generate fallback text from alt or fallback prop
  const generateFallback = () => {
    if (fallback) return fallback;
    if (alt) {
      // Extract initials from alt text
      return alt
        .split(' ')
        .map(word => word.charAt(0))
        .join('')
        .toUpperCase()
        .slice(0, 2);
    }
    return '?';
  };

  const handleImageError = () => {
    setImageError(true);
  };

  const avatarClasses = `
    relative inline-flex items-center justify-center
    rounded-full bg-gray-100 text-gray-600 border
    overflow-hidden select-none
    ${sizeClasses[size]}
    ${className}
  `.trim();

  return (
    <div className={avatarClasses} {...props}>
      {src && !imageError ? (
        <img
          src={src}
          alt={alt || 'Avatar'}
          className="h-full w-full object-cover"
          onError={handleImageError}
        />
      ) : children ? (
        children
      ) : (
        <span className="select-none">
          {generateFallback()}
        </span>
      )}
    </div>
  );
};

// Avatar Group component for multiple avatars
const AvatarGroup = ({
  children,
  max = 5,
  className = '',
  size = 'md',
  ...props
}) => {
  const childrenArray = React.Children.toArray(children);
  const visibleChildren = childrenArray.slice(0, max);
  const remainingCount = childrenArray.length - max;

  const groupClasses = `
    flex items-center space-x-3.5
    ${className}
  `.trim();

  return (
    <div className={groupClasses} {...props}>
      {visibleChildren.map((child, index) =>
        React.cloneElement(child, {
          key: index,
          size,
          className: `ring-2 ring-white ${child.props.className || ''}`
        })
      )}
      {remainingCount > 0 && (
        <Avatar
          size={size}
          fallback={`+${remainingCount}`}
          className="ring-2 ring-white bg-gray-200 text-gray-500"
        />
      )}
    </div>
  );
};

export default Avatar;
export { AvatarGroup };
